import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import { Alert, ProgressTracker, ProgressTrackerStep } from '@sainsburys-tech/bolt'
import Image from '../../../helpers/Image/Image'
import Results from './components/Results/Results'
import * as Styled from './product-finder.styles'
import { formatFilterValues, appliedFilterNames, filterString, aggregationIdMatcher } from './product-finder.helper'
import { fetcher } from '../../../helpers/ComponentHelper'
import { isEmpty } from '../../../helpers/utils'

import { onProductFinderLoaded, onProductFinderInteraction } from './product-finder.tracking'

const ProductFinder = ({ filters, onLinkClick, searchTerm, steps, theme, 'data-test': dataTest }) => {
  const [previousResultCount, setPrevResultCount] = useState(0)
  const [currentResultCount, setCurrentResultCount] = useState(2)
  const [activeStep, setActiveStep] = useState(0)
  const [activeFilters, setActiveFilters] = useState({})
  const [aggregationResults, setAggregations] = useState([])
  const [validFilters, setValidFilters] = useState([])
  const [isError, setError] = useState(false)
  const [errorCount, setErrorCount] = useState(0)
  const [baseCategory, setBaseCategory] = useState(null)
  const [productId, setProductId] = useState(null)

  const filterParams = (filterObject) => {
    return appliedFilterNames(filterObject)
      .map((filterName) => {
        if (Array.isArray(filterObject[filterName])) {
          return filterObject[filterName].map((filterValue) => filterString(filterName, filterValue)).join('&')
        }
        return filterString(filterName, filterObject[filterName])
      })
      .join('&')
  }

  const getFetchUrl = () => {
    const baseUrl = '/finder-api/search?'
    const q = searchTerm ? `q=${searchTerm}&` : ''
    const initialFilters = filterParams(filters)
    const separator = initialFilters ? '&' : ''

    // for the filters that have a value, loop through them and apply the correct params based on if the value is an array or string
    return `${baseUrl}${q}${initialFilters}${separator}${filterParams(activeFilters)}`
  }

  const checkValidFilters = (currentStep, initialAggregations) => {
    // set a Filters state which will be a list of valid filters based on if value from res.meta.aggregations > 0
    const { filterType, filters: filterData } = steps[currentStep]

    // Aggregation check to be skipped for price as the chosen price range could be split into multiple.
    // Example if filter price range is 0 - 100, it could have multiple aggregations (0-25, 25-50, 50-100)
    const getValidFilters = formatFilterValues(filterData, filterType === 'price').filter((data) =>
      aggregationIdMatcher(initialAggregations || aggregationResults, filterType, data.values)
    )

    setValidFilters(getValidFilters)
  }

  const handleError = (err) => {
    setError(true)
    console.error('Error fetching results', err)
  }

  const resolveResults = (res, checkFilters) => {
    if (res.status !== 200) {
      handleError(res.status)
    } else {
      const { totalData, appliedFilters, aggregations } = res.meta

      // save aggregations in the local state to be used to check valid filters on step change
      setAggregations(aggregations)
      // always resolves to the correct baseCategory as it is always sent back in this format in case of category
      if (filters.category) {
        const category = appliedFilters.filter((filter) => filter.id === 'category')[0].labels[0]
        setBaseCategory(category)
      }

      setPrevResultCount(currentResultCount)
      setCurrentResultCount(totalData)
      setProductId(totalData === 1 && res.data[0].id)

      // we only want to check valid filters on initial load and step change and passing in aggregations as aggregationResults isn't defined yet
      if (activeStep === 0 && checkFilters) {
        checkValidFilters(activeStep, aggregations)
      }
    }
  }

  const fetchResults = async (checkFilters) => {
    try {
      const { json, status } = await fetcher(getFetchUrl())
      return resolveResults({ ...json, status }, checkFilters)
    } catch (err) {
      return handleError(err)
    }
  }

  useEffect(() => {
    const initialFilter = filters.category || searchTerm
    if (initialFilter) {
      onProductFinderLoaded(filters.category, searchTerm)
      fetchResults(true)
    }
  }, [filters])

  useEffect(() => {
    fetchResults()
  }, [activeFilters])

  const isFirstStep = useMemo(() => activeStep === 0, [activeStep])
  const isLastStep = useMemo(() => activeStep === steps.length - 1, [activeStep])

  const resultsText = isLastStep ? '' : 'available'
  const resultsButtonKind = isLastStep ? 'primary' : 'link'
  const addResultMargin = isLastStep ? 'right' : ''
  const resultsMargin = isFirstStep ? 'left' : addResultMargin

  const trackInteraction = (interaction) => {
    onProductFinderInteraction(
      { categoryId: filters.category, steps, activeFilters, activeStep, searchTerm },
      interaction
    )
  }

  const goToStep = (step, direction) => {
    checkValidFilters(step)
    setActiveStep(step)
    trackInteraction(direction)
  }

  const resetComponent = (event) => {
    event.preventDefault()

    setPrevResultCount(0)
    setCurrentResultCount(2)
    setActiveFilters({})
    setAggregations([])
    setError(false)
    setErrorCount(1)
    setBaseCategory(null)
    setProductId(null)
    goToStep(0, 'reset')
  }

  const clickFilter = (type, value) => {
    const lowerCaseType = type.toLowerCase()
    const clickedFilters = { ...activeFilters }
    const multipleFilterValues = value.split(',')

    if (!activeFilters[lowerCaseType]) clickedFilters[lowerCaseType] = []
    // if active filter doesn't have all of the multipleFilterValues
    if (!multipleFilterValues.every((val) => clickedFilters[lowerCaseType].includes(val))) {
      clickedFilters[lowerCaseType].push(...multipleFilterValues)
      trackInteraction('filterTick')
    } else {
      // if all of them are present
      clickedFilters[lowerCaseType] = activeFilters[lowerCaseType].filter(
        (item) => !multipleFilterValues.includes(item)
      )
      trackInteraction('filterUntick')
    }

    setActiveFilters(clickedFilters)
    // TODO: when type is "category" it doesn't get updated in the active filters state, this needs to be resolved
  }

  if ((!searchTerm && !filters.category) || isEmpty(steps)) return null

  if (isError) return <ErrorMessage resetComponent={resetComponent} errorCount={errorCount} />

  const { heading, bodyCopy, filterType } = steps[activeStep]

  return (
    <Styled.Container data-test={`${dataTest}-productFinder-container`}>
      <Styled.TopContainer data-test={`${dataTest}-progressTracker-container`}>
        <ProgressTracker data-test={`${dataTest}-progressStep-container`}>
          {steps.map((step, index) => (
            <ProgressTrackerStep
              data-test={`${dataTest}-progressStep`}
              boxShadow={theme.color.monochrome.lighter}
              label={step.name}
              index={index}
              onClick={() => goToStep(index, 'stepName')}
              active={activeStep === index}
              completed={index < activeStep}
              key={`step-${step.name}`}
            />
          ))}
        </ProgressTracker>
        <Styled.Heading data-test={`${dataTest}-progressTracker-heading`}>{heading}</Styled.Heading>
        <p data-test={`${dataTest}-progressTracker-bodyCopy`} dangerouslySetInnerHTML={{ __html: bodyCopy }} />
      </Styled.TopContainer>
      <Styled.Filters data-test={`${dataTest}-filter-container`}>
        {validFilters.map((filter, index) => {
          const { mainImage, label, values } = filter
          const isActive =
            activeFilters[filterType] !== undefined &&
            activeFilters[filterType].length > 0 &&
            values.split(',').every((value) => activeFilters[filterType].includes(value))

          const longLabel = label?.length > 24
          const additionalMedia = longLabel
            ? [
                {
                  src: mainImage.source,
                  query: `(max-width: ${theme.size.breakpoint['max-width'].xs})`,
                  width: 38,
                  height: 38
                },
                {
                  src: mainImage.source,
                  query: `(min-width: ${theme.size.breakpoint['min-width'].sm})`,
                  width: 65,
                  height: 65
                }
              ]
            : undefined

          return (
            <Styled.Filter
              data-test={`${dataTest}-filter-${index}`}
              hasLabel={!!label}
              longLabel={longLabel}
              isActive={isActive}
              onClick={() => clickFilter(filterType, values)}
              key={`filter-${values}-${index}`}
            >
              <Image
                data-test={`${dataTest}-filter-${index}-image`}
                src={mainImage.source}
                alt={mainImage.altText}
                additionalMedia={additionalMedia}
                width={longLabel ? undefined : 65}
                height={longLabel ? undefined : 65}
              />
              {label && <p data-test={`${dataTest}-filter-${index}-label`}>{label}</p>}
            </Styled.Filter>
          )
        })}
      </Styled.Filters>
      <Styled.ButtonContainer data-test={`${dataTest}-buttons-container`}>
        {!isFirstStep && (
          <Styled.BackButton
            data-test={`${dataTest}-back-button`}
            kind='link'
            prev
            onClick={() => goToStep(activeStep - 1, 'previous')}
          >
            <Styled.Chevron dir='left' />
            <span>Previous</span>
          </Styled.BackButton>
        )}
        <Results
          data-test={`${dataTest}-results-button`}
          resultsMargin={resultsMargin}
          categoryId={filters.category}
          activeFilters={activeFilters}
          currentCount={currentResultCount}
          previousCount={previousResultCount}
          productId={productId}
          baseCategory={baseCategory}
          onLinkClick={onLinkClick}
          resultsText={resultsText}
          resultsButtonKind={resultsButtonKind}
          searchTerm={searchTerm}
          trackInteraction={trackInteraction}
        />
        {!isLastStep && (
          <Styled.NavButton
            data-test={`${dataTest}-next-button`}
            kind='link'
            onClick={() => goToStep(activeStep + 1, 'next')}
          >
            <span>Next</span>
            <Styled.Chevron dir='right' />
          </Styled.NavButton>
        )}
      </Styled.ButtonContainer>
    </Styled.Container>
  )
}

const ErrorMessage = ({ resetComponent, errorCount }) => (
  <Alert kind='error' isCloseable={false} data-test='component-product-finder-alert'>
    {errorCount === 0 && (
      <Styled.ErrorContainer>
        <span>
          Sorry, there&apos;s been a technical glitch. But this&apos;ll often fix itself the second time round.
        </span>
        <Styled.TryAgainLink kind='link' onClick={(event) => resetComponent(event)}>
          Try again now
        </Styled.TryAgainLink>
      </Styled.ErrorContainer>
    )}
    {errorCount > 0 && (
      <>
        Sorry, we&apos;re still having technical issues with this feature. It&apos;s best to come back later and try
        again
      </>
    )}
  </Alert>
)

ProductFinder.propTypes = {
  filters: PropTypes.shape({
    brands: PropTypes.array,
    category: PropTypes.string,
    'customer-rating': PropTypes.string,
    price: PropTypes.array
  }),
  onLinkClick: PropTypes.func,
  searchTerm: PropTypes.string,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      heading: PropTypes.string.isRequired,
      bodyCopy: PropTypes.string.isRequired,
      filterType: PropTypes.string.isRequired,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          values: PropTypes.string.isRequired,
          mainImage: PropTypes.shape({
            source: PropTypes.string.isRequired,
            altText: PropTypes.string.isRequired
          })
        })
      )
    })
  )
}

ProductFinder.defaultProps = {
  searchTerm: '',
  onLinkClick: null,
  steps: [],
  filters: {}
}

export default withTheme(ProductFinder)
